import { useEvent } from 'effector-react';
import { generateUuid } from '@lib/uuid';
import { UserUploadedFile } from '@nimbus/app-analytics-client/lib/events/UserUploadedFile';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { useAnalytics } from '@nx-frontend/shared/app-analytics';
import {
  deleteUploadFileEvent,
  endUploadFileEvent,
  startUploadFileEvent,
  updateUploadFileEvent,
  uploadFileFx,
  upsertFileEvent,
} from '@nx-frontend/shared/store/feature-files';
import { BucketAttachment, mapBucketFileDtoToBucketFile, UploadFileStatus } from '@nx-frontend/shared/types';
import { UploadContext } from '../model';

export interface FilesUploadParams {
  files: File[];
  context: UploadContext;
}

const maxFileSize = 1000 * 1024 * 1024 * 5; // 5GB

interface FilesUploadProps {
  onSuccess?: (result: BucketAttachment) => void;
  onError?: () => void;
  autoCloseUploadModal?: boolean;
}

export const useFilesUpload = ({ onSuccess, onError, autoCloseUploadModal }: FilesUploadProps = {}): ((
  payload: FilesUploadParams
) => void) => {
  const [uploadFile, startUploadFile, updateUploadFile, endUploadFile, upsertFile, deleteUploadFile] = useEvent([
    uploadFileFx,
    startUploadFileEvent,
    updateUploadFileEvent,
    endUploadFileEvent,
    upsertFileEvent,
    deleteUploadFileEvent,
  ]);
  const { toast } = useToast();
  const { analytics } = useAnalytics();

  return async ({ context, files }: FilesUploadParams) => {
    let filesCountOverLimit = 0;
    files.forEach((file) => {
      if (isFileOverSizeLimit(file.size)) {
        filesCountOverLimit++;
        return;
      }

      const id = generateUuid();

      let fileCanceler = undefined;

      const sessionId = context.sessionId;
      if (!sessionId) {
        throw Error('Session id not provided');
      }

      const params = {
        ...getUploadParams(context),
        file,
        sessionId: sessionId,
        workspaceId: context.workspaceId,
        id,
      };
      const fileData = {
        id,
        size: file.size,
        title: file.name,
        status: UploadFileStatus.IN_PROGRESS,
        uploadedSize: 0,
        createdAt: new Date().getTime(),
        target: params.target,
        targetId: params.targetId,
      };

      uploadFile({
        ...params,
        onUploadProgress: (progressEvent) => {
          updateUploadFile({ id, uploadedSize: progressEvent.loaded, size: progressEvent.total });
        },
        getCancelToken: (canceler) => {
          fileCanceler = canceler;
        },
      })
        .then((result) => {
          upsertFile({ ...mapBucketFileDtoToBucketFile(result.file), pageId: context.pageId, isNew: true });
          endUploadFile({ id, status: UploadFileStatus.DONE });
          if (autoCloseUploadModal) {
            setTimeout(() => {
              deleteUploadFile(id);
            }, 2000);
          }

          analytics.sendEvent(
            new UserUploadedFile({
              fileId: result.file.globalId,
              fileName: result.file.filename,
              fileUrl: result.file.url,
              asString: `${result.file.filename} (${result.file.globalId})`,
            })
          );

          onSuccess?.(result);
        })
        .catch((err) => {
          if (err.code === 'ERR_CANCELED') {
            endUploadFile({ id, status: UploadFileStatus.CANCELED });
          } else {
            console.warn('[ATTACHMENT_UPLOAD_ERROR]: ', err);
            endUploadFile({ id, status: UploadFileStatus.ERROR });
          }

          if (autoCloseUploadModal) {
            setTimeout(() => {
              deleteUploadFile(id);
            }, 2000);
          }
          onError?.();
        });

      startUploadFile({ ...fileData, canceler: fileCanceler });
    });

    if (filesCountOverLimit === 1 && files.length === 1) {
      toast({ title: 'The file size cannot exceed 5GB.' });
    } else if (filesCountOverLimit) {
      toast({
        title: `${filesCountOverLimit} out of ${files.length} files will not be uploaded, as their size exceeds 5GB.`,
      });
    }
  };
};

const isFileOverSizeLimit = (size: number) => {
  return size > maxFileSize;
};

function getUploadParams(context: UploadContext) {
  if (context.blockId) {
    let groupId = `portal#${context.portalId}`;
    if (context.pageId) {
      groupId += `#page#${context.pageId}`;
    }
    return {
      target: context.target ?? 'portalBlock',
      targetId: context.blockId,
      groupId,
    };
  } else {
    return {
      target: context.target ?? 'portalFiles',
      targetId: context.portalId,
      groupId: `portal#${context.portalId}`,
    };
  }
}
